import React from 'react';
import { useUserTeam } from 'context/user/UserContext';
import useUserAvatar from 'components/UserAvatar/useUserAvatar';
import { findTeamMember } from 'components/Team/teamUtils';
import Tooltip from 'components/Tooltip';
import paletteV2 from '../Typography/typographyPalette';

export type ProfileBubbleSize = 'sm' | 'md' | 'lg';

const PROFILE_BUBBLE_DIMENSIONS: Record<ProfileBubbleSize, number> = {
  sm: 20,
  md: 24,
  lg: 32,
};

interface ProfileBubbleProps {
  userId: string;
  initials: string;
  size?: ProfileBubbleSize;
  className?: string;
}

interface UserProfileBubbleProps {
  userId: string;
  userInitials?: string;
  size?: ProfileBubbleSize;
  tooltipText?: string;
  showTooltip?: boolean;
}

type PaletteV2 = typeof paletteV2;
type ColorVariant = keyof PaletteV2;

const profileBubbleColors: Array<ColorVariant> = [
  'lavender',
  'azure',
  'papaya',
  'sunflower',
  'chartreuse',
  'sapphire',
  'ocean',
  'orchid',
  'hibiscus',
  'punch',
];

export const getProfileBubbleColor = (userId: string): string => {
  const idStr = userId.replace(/\D/g, '');
  const lastDigit =
    idStr.length > 0 ? parseInt(idStr[idStr.length - 1], 10) : 0;
  const colorVariant =
    profileBubbleColors[lastDigit % profileBubbleColors.length];

  return paletteV2[colorVariant][500 as keyof PaletteV2[typeof colorVariant]];
};

export const ProfileBubble = ({
  userId,
  initials,
  size = 'md',
  className,
}: ProfileBubbleProps) => {
  const dimension = PROFILE_BUBBLE_DIMENSIONS[size];

  return (
    <div
      className={className}
      style={{
        backgroundColor: getProfileBubbleColor(userId),
        color: 'white',
        fontSize: size === 'lg' ? 13 : 11,
        fontWeight: 'bold',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textTransform: 'uppercase',
        height: dimension,
        width: dimension,
        minWidth: dimension,
      }}
    >
      {initials}
    </div>
  );
};

const UserProfileBubble = ({
  userId,
  userInitials,
  size = 'md',
  tooltipText,
  showTooltip = true,
}: UserProfileBubbleProps) => {
  const userTeam = useUserTeam();
  const userData = useUserAvatar();

  const foundMember = findTeamMember(userTeam, userId);

  const initials =
    foundMember?.initials ||
    userInitials ||
    (userId === userData.userId ? userData.initials : '');

  const tooltip = tooltipText || foundMember?.name || userData.name;

  const bubble = (
    <ProfileBubble userId={userId} initials={initials} size={size} />
  );

  if (!showTooltip) {
    return bubble;
  }

  return (
    <Tooltip text={tooltip ?? ''} placement="top">
      {bubble}
    </Tooltip>
  );
};

export default UserProfileBubble;
